import { DOMParser } from "@xmldom/xmldom";
import PizZip from "pizzip";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DOCUMENT_TYPES } from "~/config/documents";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import { GenerateApi } from "~/functions/api/generate";
import close from "~/images/close.svg";
import ExternalLinkIcon from "~/images/external-site.svg?react";
import { setCurrentProjectOutputsDocumentType, setOrganization } from "~/slices/auth";
import type { RootState } from "~/store";
import { generateGroupName, joinGroup, leaveGroup } from "~/utils/webPubSubUtils";
import styles from "./index.module.scss";

const MAX_CHAR_LIMIT = 60000;
interface ImportSelectionModalProps {
  onClose: () => void;
}

export function DownloadMinutesButton() {
  const MINUTES_GUIDE_URL =
    "https://drive.google.com/file/d/1JyI2Gl5zLUWJXN_6i0lzFe_iqDjBeoB7/view?usp=sharing";

  return (
    <a
      href={MINUTES_GUIDE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="flex mt-4 items-center bg-transparent border-none p-0 no-underline gap-1 cursor-pointer"
    >
      <ExternalLinkIcon />
      <span className="text-lightblue text-xs underline">議事録作成のポイントを確認する</span>
    </a>
  );
}

const SelectionModal: React.FC<ImportSelectionModalProps> = ({ onClose }) => {
  const [textInput, setTextInput] = useState<string>("");
  const { currentUser, currentProject, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const { client } = useWebPubSub();
  const dispatch = useDispatch();

  // 残り文字数の計算
  const characterCount = useMemo(() => textInput.length, [textInput]);
  // 文字数制限を超えているかをチェック
  useEffect(() => {
    setIsLimitExceeded(characterCount > MAX_CHAR_LIMIT);
  }, [characterCount]);

  const processFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split(".").pop();

      if (!["txt", "md", "docx"].includes(fileExtension || "")) {
        toast.error(TOAST_MESSAGES.SUMMARY.INVALID_FILE_FORMAT);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          if (fileExtension === "txt" || fileExtension === "md") {
            const content = event.target?.result as string;
            if (content.length > MAX_CHAR_LIMIT) {
              toast.warning(
                `ファイルの内容が最大文字数（${MAX_CHAR_LIMIT}字）を超えています。一部の内容は切り捨てられます。`,
              );
              resolve(content.substring(0, MAX_CHAR_LIMIT));
            } else {
              resolve(content);
            }
          } else if (fileExtension === "docx") {
            const content = event.target?.result as ArrayBuffer;
            const paragraphs = getParagraphs(content);
            const joinedText = paragraphs.join("\n");
            if (joinedText.length > MAX_CHAR_LIMIT) {
              toast.warning(
                `ファイルの内容が最大文字数（${MAX_CHAR_LIMIT}字）を超えています。一部の内容は切り捨てられます。`,
              );
              resolve(joinedText.substring(0, MAX_CHAR_LIMIT));
            } else {
              resolve(joinedText);
            }
          } else {
            reject("未知のファイル形式です");
          }
        } catch (_error) {
          reject(`処理エラー: ${file.name}`);
        }
      };

      reader.onerror = () => {
        reject(`ファイル読み込みエラー: ${file.name}`);
      };

      if (fileExtension === "docx") {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        try {
          const files = Array.from(acceptedFiles);
          const results = await Promise.allSettled(files.map((file) => processFile(file)));

          const fileContents = results
            .filter((result) => result.status === "fulfilled")
            .map((result) => (result as PromiseFulfilledResult<string>).value);

          if (fileContents.length > 0) {
            // 複数ファイルの場合、合計文字数が制限を超えないように調整
            let combinedContent = "";
            for (const content of fileContents) {
              if (combinedContent.length + content.length + 2 <= MAX_CHAR_LIMIT) {
                combinedContent += (combinedContent ? "\n\n" : "") + content;
              } else {
                const remaining = MAX_CHAR_LIMIT - combinedContent.length - 2;
                if (remaining > 0) {
                  combinedContent +=
                    (combinedContent ? "\n\n" : "") + content.substring(0, remaining);
                }
                toast.warning(
                  `複数ファイルの合計が最大文字数（${MAX_CHAR_LIMIT}字）を超えています。一部の内容は含まれません。`,
                );
                break;
              }
            }
            setTextInput(combinedContent);
          } else {
            toast.error(TOAST_MESSAGES.SUMMARY.ALL_FILES_FAILED);
          }
        } catch (_error) {
          toast.error(TOAST_MESSAGES.SUMMARY.UNEXPECTED_ERROR);
        }
      } else {
        toast.error(TOAST_MESSAGES.SUMMARY.NO_FILE_SELECTED);
      }
    },
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((file) => {
        toast.error(TOAST_MESSAGES.SUMMARY.INVALID_FILE_TYPE(file.file.name));
      });
    },
    accept: {
      "text/plain": [".txt"],
      "text/markdown": [".md"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        const results = await Promise.allSettled(files.map((file) => processFile(file)));

        const fileContents = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => (result as PromiseFulfilledResult<string>).value);

        if (fileContents.length > 0) {
          // 複数ファイルの場合、合計文字数が制限を超えないように調整
          let combinedContent = "";
          for (const content of fileContents) {
            if (combinedContent.length + content.length + 2 <= MAX_CHAR_LIMIT) {
              combinedContent += (combinedContent ? "\n\n" : "") + content;
            } else {
              const remaining = MAX_CHAR_LIMIT - combinedContent.length - 2;
              if (remaining > 0) {
                combinedContent +=
                  (combinedContent ? "\n\n" : "") + content.substring(0, remaining);
              }
              toast.warning(
                `複数ファイルの合計が最大文字数（${MAX_CHAR_LIMIT}字）を超えています。一部の内容は含まれません。`,
              );
              break;
            }
          }
          setTextInput(combinedContent);
        } else {
          toast.error(TOAST_MESSAGES.SUMMARY.ALL_FILES_FAILED);
        }
      } else {
        toast.error(TOAST_MESSAGES.SUMMARY.NO_FILE_SELECTED);
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.SUMMARY.UNEXPECTED_ERROR);
    } finally {
      if (e.target) {
        e.target.value = ""; // 初回ファイル選択のリセット
      }
    }
  };

  // テキスト入力の変更ハンドラー
  const handleTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // 制限を超えている場合は通知するが、入力は許可する
    if (newValue.length > MAX_CHAR_LIMIT) {
      toast.info(TOAST_MESSAGES.SUMMARY.MAX_CHAR_EXCEEDED(MAX_CHAR_LIMIT));
    }
    setTextInput(newValue);
  };

  function str2xml(str: any) {
    if (str.charCodeAt(0) === 65279) {
      // BOM sequence
      str = str.substr(1);
    }
    return new DOMParser().parseFromString(str, "text/xml");
  }

  function getParagraphs(content: ArrayBuffer): string[] {
    const zip = new PizZip(content);
    const xml = str2xml(zip.files["word/document.xml"]!.asText());
    const paragraphsXml = xml.getElementsByTagName("w:p");
    const paragraphs: string[] = []; // すべての段落を格納

    for (let i = 0; i < paragraphsXml.length; i++) {
      let fullText = "";
      const textsXml = paragraphsXml[i]!.getElementsByTagName("w:t");
      for (let j = 0; j < textsXml.length; j++) {
        const textXml = textsXml[j];
        if (textXml?.childNodes?.[0]) {
          fullText += textXml.childNodes[0].nodeValue;
        }
      }
      if (fullText) {
        paragraphs.push(fullText);
      }
    }
    return paragraphs;
  }

  const handleGenerateSummary = async () => {
    if (isLimitExceeded) {
      toast.error(
        `テキストが最大文字数（${MAX_CHAR_LIMIT}字）を超えています。内容を短くしてください。`,
      );
      return;
    }

    setIsLoading(true);
    const generateApi = new GenerateApi();

    const requestBody = {
      input_data: {
        content: textInput,
      },
    };

    // APIコール前にグループ参加（バックエンドが高速完了してもメッセージを受信できるよう先行参加）
    const groupName = generateGroupName(currentProject.id, "summary", "generate");
    if (client !== null) {
      await joinGroup(client, groupName);
    }

    const response = await generateApi.generateSummary(
      currentUser.organization_id,
      currentProject.id,
      requestBody,
    );

    if (response === true) {
      // Update organization's remaining credit locally
      if (organization) {
        const creditCost = DOCUMENT_TYPES.summary.creditUsage.generate;
        dispatch(
          setOrganization({
            ...organization,
            remainingCredit: Math.max(0, organization.remainingCredit - creditCost),
          }),
        );
      }
      // 生成開始時にisGeneratingをtrueに設定
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType: "summary" as keyof typeof DOCUMENT_TYPES,
          documentId: "", // 生成中のため空文字列
          isGenerating: true,
        }),
      );

      toast.success(TOAST_MESSAGES.SUMMARY.GENERATE_REQUEST_SUCCESS);
      onClose();
    } else {
      // 失敗時クリーンアップ
      if (client !== null) leaveGroup(client, groupName);
    }

    setIsLoading(false);
  };

  // 選択画面を表示
  return (
    <div className={styles["modalOverlay"]}>
      <div className={styles["modalContent"]}>
        <div className={styles["modalHeader"]}>
          <h2>ドキュメントのアップロード</h2>
          <button className={styles["closeButton"]} onClick={onClose}>
            <img src={close} alt="閉じる" />
          </button>
        </div>
        <div className={styles["importActions"]}>
          <div className={styles["modalBody"]}>
            <div className={styles["file-select-label"]}>
              <div>
                <h3>ファイルを選択(.txt, .md, .docx)</h3>
              </div>
              <div style={{ fontSize: "14px", color: "#5d5951" }}>
                最大 {MAX_CHAR_LIMIT.toLocaleString()} 文字まで
              </div>
            </div>

            <div
              {...getRootProps({
                className: `${styles["file-upload-background"]} ${
                  isDragActive ? styles["drag-active"] : ""
                }`,
              })}
            >
              <input {...getInputProps()} />
              <div className={styles["fileUpload"]}>
                {isDragActive ? (
                  <p>ここにドロップしてください...</p>
                ) : (
                  <>
                    <span>ファイルを選択</span>
                    <span>ドラッグ&ドロップ</span>
                    <span className={styles["fileLabel"]}>
                      ファイル選択
                      <input type="file" onChange={handleFileChange} disabled={isLoading} />
                    </span>
                    <span>ファイル形式: .txt, .md, .docx</span>
                  </>
                )}
              </div>
            </div>
            <DownloadMinutesButton />
            <div className={styles["icon-or-text"]}></div>

            <div className={styles["textInputSection"]}>
              <div className={styles["textInputHeader"]}>
                <h3>テキストを入力</h3>
                <div
                  className={`${styles["charCounter"]} ${isLimitExceeded ? styles["exceeded"] : ""}`}
                >
                  {characterCount.toLocaleString()} / {MAX_CHAR_LIMIT.toLocaleString()} 文字
                </div>
              </div>

              <textarea
                placeholder="テキスト情報の内容を入力"
                value={textInput}
                onChange={handleTextInputChange}
                disabled={isLoading}
                className={isLimitExceeded ? styles["exceededInput"] : ""}
              />
            </div>
          </div>
        </div>
        <div className={styles["actions"]}>
          <button className={styles["cancelButton"]} onClick={onClose} disabled={isLoading}>
            キャンセル
          </button>
          <button
            className={styles["importButton"]}
            onClick={handleGenerateSummary}
            disabled={!textInput || isLoading || isLimitExceeded}
          >
            {isLoading ? "生成中..." : "議事録を生成"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectionModal;
